import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';

import {
  HoldingAvailability,
  HoldingsAvailabilityDto,
  RecordAvailabilityGeneralStatus,
  RecordAvailabilityIconColor,
} from '@app/components';
import { AvailabilityContext, RecordAvailability } from '@app/contexts';
import { makeRequest } from '@app/utils';

export const AvailabilityProvider: FC = ({ children }) => {
  const [availability, setAvailability] = useState<
    Record<string, RecordAvailability>
  >({});
  const [ids, setIds] = useState<string[]>([]);
  const [isFirstPage, setIsFirstPage] = useState(false);

  const getAvailability = useCallback(
    (requestedId: string[], currentPageNumber) => {
      setIds(requestedId);
      setIsFirstPage(currentPageNumber === 1);
    },
    []
  );

  const availabilityQuery = useQuery(
    ['availability', ids],
    () =>
      makeRequest<{ holdings: HoldingsAvailabilityDto[] }>({
        url: '/opac-rtac/rtac',
        params: {
          fullPeriodicals: true,
          instanceIds: ids.join(','),
        },
      }),
    { enabled: !isEmpty(ids) }
  );

  const processAvailability = useCallback(
    (holdings: HoldingsAvailabilityDto[] = []) => {
      return ids.reduce((newAvailability, id) => {
        const currentInstanceAvailability = holdings.find(
          holding => holding?.instanceId === id
        );
        const holdingsAvailabilityData: HoldingAvailability[] =
          currentInstanceAvailability?.holdings ?? [];

        const availableHoldings = holdingsAvailabilityData.filter(
          holding =>
            holding.status.generalStatus ===
            RecordAvailabilityGeneralStatus.available
        );

        const getSummaryAvailabilityStatus = () => {
          if (availabilityQuery.isError) {
            return {
              generalStatus: RecordAvailabilityGeneralStatus.error,
              customDisplayStatus: RecordAvailabilityGeneralStatus.error,
              customIcon: RecordAvailabilityIconColor.error,
            };
          }

          return !isEmpty(availableHoldings)
            ? {
                generalStatus: RecordAvailabilityGeneralStatus.available,
                customDisplayStatus: RecordAvailabilityGeneralStatus.available,
                customIcon: RecordAvailabilityIconColor.green,
              }
            : {
                generalStatus: RecordAvailabilityGeneralStatus.notAvailable,
                customDisplayStatus:
                  RecordAvailabilityGeneralStatus.notAvailable,
                customIcon: RecordAvailabilityIconColor.red,
              };
        };

        const summaryAvailability = {
          status: getSummaryAvailabilityStatus(),
          isRequestable: currentInstanceAvailability?.requestable,
          isRTACApplicable: currentInstanceAvailability?.isRTACApplicable,
          hasVolumes: holdingsAvailabilityData.some(holdingAvailability =>
            Boolean(holdingAvailability.volume)
          ),
          ...(!availabilityQuery.isError && {
            copiesRemaining: {
              available: availableHoldings.length,
              total: holdingsAvailabilityData.length,
            },
          }),
        };

        newAvailability[id] = {
          availabilityData: holdingsAvailabilityData,
          summaryAvailability,
        };

        return newAvailability;
      }, {});
    },
    [availabilityQuery.isError, ids]
  );

  useEffect(() => {
    if (availabilityQuery.isFetched) {
      setAvailability(prevState => {
        const processedAvailability = processAvailability(
          availabilityQuery.data?.holdings
        );

        return isFirstPage
          ? processedAvailability
          : { ...prevState, ...processedAvailability };
      });
    }
  }, [
    availabilityQuery.data,
    availabilityQuery.isFetched,
    ids,
    isFirstPage,
    processAvailability,
  ]);

  const value = useMemo(
    () => ({
      getAvailability,
      availability,
      isAvailabilityLoading: availabilityQuery.isLoading,
    }),
    [availability, getAvailability, availabilityQuery.isLoading]
  );

  return (
    <AvailabilityContext.Provider value={value}>
      {children}
    </AvailabilityContext.Provider>
  );
};
